import { useAtom } from '@reatom/npm-react'
import styled from 'styled-components'
import { useMemo } from 'use-memo-one'

import { useFormatDuration } from '@root/shared'
import { useTranslation } from '@root/shared/localization'

import { StatsLabel, StatsPanel } from './dev-tokens-common'
import { DevTokensHighlightToken } from './dev-tokens-highlight-token/dev-tokens-highlight-token'

import type { PairModel } from '@root/entities/pair'
import type { PairCreatorStatsTopToken } from '@root/entities/pair/atoms/reatom-pair-creator-stats'

type Props = {
  pair: PairModel
}

export const DevTokensHighlights: React.FC<Props> = ({ pair }) => {
  const { t } = useTranslation()
  const [creatorStats] = useAtom(pair.creatorStats)
  const { formatDuration } = useFormatDuration()

  const topTokenByAthMarketCap = useMemo<
    PairCreatorStatsTopToken | undefined
  >(() => {
    if (!creatorStats?.topTokens.length) {
      return undefined
    }
    return creatorStats.topTokens.reduce((acc, token) =>
      token.athMarketCap.isGreaterThan(acc.athMarketCap) ? token : acc,
    )
  }, [creatorStats?.topTokens])

  const lastLaunchedAgo = useMemo(() => {
    if (!creatorStats?.lastTokenLaunchedAt) {
      return undefined
    }
    return formatDuration({
      startAt: creatorStats.lastTokenLaunchedAt,
      mode: 'singleUnitShort',
      withAgo: true,
    })
  }, [creatorStats?.lastTokenLaunchedAt, formatDuration])

  if (!topTokenByAthMarketCap && !lastLaunchedAgo) {
    return undefined
  }

  return (
    <Root>
      <StatsLabel>{t('dev-tokens.highlights')}:</StatsLabel>
      <DevTokensHighlightToken pair={pair} />
      {lastLaunchedAgo ? (
        <Row>
          <Key>{t('dev-tokens.last-token-launched')}:</Key>
          <ValueContainer>
            <Value>{lastLaunchedAgo}</Value>
          </ValueContainer>
        </Row>
      ) : undefined}
    </Root>
  )
}

const Root = styled(StatsPanel)`
  display: flex;
  flex-direction: column;
  gap: 6px;

  font-size: 12px;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  line-height: 12px;
`
const Key = styled.span`
  color: var(--text-10);
`
const ValueContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`
const Value = styled.span`
  color: var(--text-12);
  font-weight: 600;
`
